import { default as config } from "common/config/config";
import { useMemo } from "react";
import { useQueryClient } from "react-query";
import DataTable from "ui/common/components/display/DataTable/DataTable";
import useSearchParam from "ui/common/hooks/useSearchParam";
import useTableSearchParamPagination from "ui/common/hooks/useTableSearchParamPagination";
import useTableSearchParamSorting from "ui/common/hooks/useTableSearchParamSorting";
import useTranslate from "ui/locale/hooks/useTranslate";
import { Queries } from "ui/query/queries";
import GiftCardsRightToolbar from "./GiftCardsRightToolbar";
import { GIFTCARDS_COLUMNS_KEYS, useGiftCardsColumns } from "ui/giftcards/hooks/useGiftCardsColumns";
import useGetAllGiftCards from "features/giftcards/hooks/useGetLocalGiftCards";
import useGetGiftCardStock from "features/giftcards/hooks/useGetGiftCardStock";
import { GiftCardsTableRoot } from "./GiftCardsTable.styles";
import GiftCardsLeftToolbar from "./GiftCardsLeftToolbar";
import { useGiftCardStockColumns } from "ui/giftcards/hooks/useGiftCardStockColumns";
import { GiftCardStockStatus } from "common/models/api/models/GiftCardStock";

export default function GiftCardsTable() {
    const columns = useGiftCardsColumns();
    const stockColumns = useGiftCardStockColumns();
    const queryClient = useQueryClient();
    const [query] = useSearchParam("query");
    const [type] = useSearchParam("type");
    const giftCardType = type ?? GiftCardStockStatus.onDemand;
    const [partner] = useSearchParam("partner");
    const [pagination, setPagination] = useTableSearchParamPagination();
    const [{ order, orderBy }, setSorting] = useTableSearchParamSorting(...GIFTCARDS_COLUMNS_KEYS);

    const sortingState = useMemo(() => [{ id: orderBy, desc: order === "DESC" }], [orderBy, order]);

    const translate = useTranslate();

    const isStock = giftCardType === GiftCardStockStatus.stock;

    const {
        data: { items = [], pages = 0, currentPage = 1, totalItems } = {},
        isLoading: isLoadingGiftCards,
        isRefetching: isRefetchingGiftCards,
    } = useGetAllGiftCards({ query, page: pagination.pageIndex, partner }, { enabled: !isStock });

    const {
        data: { items: stockItems = [], pages: stockPages = 0, currentPage: stockCurrentPage = 1, totalItems: stockTotalItems } = {},
        isLoading: isLoadingStock,
        isRefetching: isRefetchingStock,
    } = useGetGiftCardStock(
        { page: pagination.pageIndex, pageSize: config.paginatedPageSizes.releaseStrategy, query: partner },
        { enabled: isStock },
    );

    const handleRefresh = () => {
        if (isStock) {
            queryClient.refetchQueries(Queries.GIFTCARDS_STOCK_FIND_ALL);
        } else {
            queryClient.refetchQueries(Queries.GIFTCARDS_FIND_ALL);
        }
    };

    return (
        <GiftCardsTableRoot>
            <DataTable
                rightToolbar={<GiftCardsRightToolbar />}
                leftToolbar={<GiftCardsLeftToolbar />}
                onRefresh={handleRefresh}
                isLoading={isStock ? isLoadingStock : isLoadingGiftCards}
                refreshing={isStock ? isRefetchingStock : isRefetchingGiftCards}
                title={translate("giftCards")}
                pageSize={config.paginatedPageSizes.releaseStrategy}
                pageCount={isStock ? stockPages : pages}
                data={isStock ? stockItems : items}
                sorting={sortingState}
                columns={isStock ? stockColumns : columns}
                onPaginationChange={setPagination}
                onSortingChange={setSorting}
                manualSorting
                manualPagination
                pageIndex={isStock ? stockCurrentPage - 1 : currentPage - 1}
                rowCount={isStock ? stockTotalItems : totalItems}
                search={!isStock}
            />
        </GiftCardsTableRoot>
    );
}
